"use client";

import { useEffect, useMemo, useState } from "react";
import type { BoxCode, Variety } from "@/lib/catalog/types";
import {
  boxCapacityForLength,
  effectiveDefaultLength,
  pricePerStem,
  stemsInBox,
} from "@/lib/catalog/pricing";
import { getDefaultBoxUnit } from "@/lib/catalog/box-unit-defaults";
import { mixAllowedForUnit, leadColor } from "@/lib/catalog/color-selection";
import {
  readLengthPreference,
  writeLengthPreference,
} from "@/lib/cart/preferences";
import { usePreferences } from "@/stores/preferences";

// Step 6.6: Stem unit removed from the detail page. Locally narrow
// UnitChoice to bunch | box.
type UnitChoice = "bunch" | "box";
import { addToBox } from "@/lib/cart/cart";
import { readCart, writeCart } from "@/lib/cart/storage";
import { readVarietyPref, writeVarietyPref } from "@/lib/catalog/preferences";
import { LengthPicker } from "./LengthPicker";
import {
  ColorPicker,
  type ColorChoice,
  colorChoiceLabel,
} from "@/components/catalog/ColorPicker";
import { QuickAddToast } from "@/components/catalog/QuickAddToast";
import { usePriceGate } from "@/components/auth/usePriceGate";
import {
  SignInForPricesCTA,
  PriceGateSkeleton,
} from "@/components/catalog/SignInForPricesCTA";
import { useRouter, usePathname } from "next/navigation";
import { isSoldOut } from "@/lib/availability/keys";

const ALL_BOX_CODES: BoxCode[] = ["EB", "QB", "HB"];

const COMPAT_LABEL: Record<string, string> = {
  A: "Garden Roses",
  B: "Spray Roses",
  C: "Hydrangeas",
  D: "Specialty",
  E: "Fillers & Greens",
};

function applyColorOverride(
  variety: Variety,
  colorChoice?: string,
): Variety {
  if (!colorChoice || !variety.priceByColor) return variety;
  let override: number | undefined;
  if (colorChoice === "mix") {
    const all = Object.values(variety.priceByColor);
    if (all.length > 0) override = Math.min(...all);
  } else {
    override = variety.priceByColor[colorChoice];
  }
  if (override === undefined) return variety;
  return {
    ...variety,
    pricePerStem: variety.pricePerStem.map((p) => ({
      length: p.length,
      usd: override!,
    })),
  };
}

function colorTitle(c: string): string {
  return c
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

export function DetailPricingPanel({
  variety,
  soldOutKeys,
}: {
  variety: Variety;
  // #452: the active sold-out keys for THIS variety (variety/color/length
  // scopes), passed from the server detail page. Empty/omitted = all available.
  soldOutKeys?: string[];
}) {
  const isHydrangea = variety.purchaseUnit === "box";
  // Step 6.7.1 fix: only roses get a length picker on detail. Specialty
  // varieties had `lengths` populated for legacy data reasons but lengths
  // don't drive their price or capacity, so the picker was leaking onto
  // Delphinium / Asiatic Lily / Oriental Lily / Alstroemeria detail pages.
  const showLengthPicker =
    variety.category === "rose" && variety.lengths.length > 1 && !isHydrangea;
  // Step 6.6: Stem unit removed from the toggle. Hydrangea locks to Box.
  const availableUnits: UnitChoice[] = isHydrangea ? ["box"] : ["bunch", "box"];
  const hasColorPicker =
    Array.isArray(variety.colorOptions) && variety.colorOptions.length > 0;
  // #464: default to a color that is actually offered. Keep colorTag when it's
  // in colorOptions (unchanged for existing varieties); otherwise fall to the
  // first option - matching QuickAddPopover's Spec-5g default - so a variety
  // whose hero colorTag is not an orderable color (e.g. ranunculus/scabiosa,
  // and the pre-existing stock case) never defaults to an unofferable color.
  // #468 (Alexis): by-bunch default = the variety's LEAD color (colorTag when
  // offered, else the first option), never Mix.
  const [colorChoice, setColorChoice] = useState<ColorChoice>(
    hasColorPicker ? (leadColor(variety) as ColorChoice) : "",
  );
  const effectiveVariety = applyColorOverride(
    variety,
    colorChoice || undefined,
  );

  const [length, setLength] = useState<number>(() => effectiveDefaultLength(variety));
  // Step 6.7.3 / AC173-AC174: detail-page Buy-by toggle is a view of the
  // global usePreferences store, not local state. The Zustand store is
  // persisted to localStorage so the user's choice survives across
  // detail-page navigations and reloads. Per-variety memory still
  // overrides (hydration effect below promotes per-variety saved unit
  // into the global store).
  const globalUnit = usePreferences((s) => s.unitPreference);
  const setGlobalUnit = usePreferences((s) => s.setUnitPreference);
  // Hydrangea is always Box regardless of global preference.
  const unit: UnitChoice = isHydrangea ? "box" : globalUnit;
  // #468 (Alexis): Mix is by-box ONLY. Coerce Mix -> lead color when buying by
  // bunch so a single bunch is never "Mix".
  useEffect(() => {
    if (unit === "bunch" && colorChoice === "mix") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setColorChoice(leadColor(variety) as ColorChoice);
    }
  }, [unit, colorChoice, variety]);
  const [quantity, setQuantity] = useState<number>(1);

  const capacity = useMemo(
    () => boxCapacityForLength(variety, length),
    [variety, length],
  );
  const availableBoxCodes = useMemo(() => {
    if (!capacity) return [] as BoxCode[];
    return ALL_BOX_CODES.filter((c) => capacity[c] !== null);
  }, [capacity]);
  const [boxCode, setBoxCode] = useState<BoxCode | null>(() => {
    if (!capacity) return null;
    const saved =
      typeof window !== "undefined" ? readVarietyPref(variety.slug) : null;
    return getDefaultBoxUnit(variety, saved?.boxCode ?? null);
  });
  useEffect(() => {
    if (!capacity) return;
    if (boxCode === null || capacity[boxCode] === null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- align to current capacity
      setBoxCode(getDefaultBoxUnit(variety, null));
    }
  }, [capacity, boxCode, variety]);

  /* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps -- one-time hydration of UI prefs */
  useEffect(() => {
    // Step 6.7 section 7c: per-variety memory takes precedence over the
    // older global length/unit prefs.
    const perVariety = readVarietyPref(variety.slug);
    if (perVariety) {
      if (perVariety.length && variety.lengths.includes(perVariety.length)) {
        setLength(perVariety.length);
      }
      // 12.8: do NOT push a per-variety saved unit into the GLOBAL toggle.
      // Opening a variety saved as "bunch" used to flip box/bunch mode for the
      // whole catalog. The toggle is the buyer's explicit catalog-wide mode;
      // per-variety memory keeps size/length/qty/color only.
      if (perVariety.qty && perVariety.qty > 0) setQuantity(perVariety.qty);
      if (
        perVariety.color &&
        hasColorPicker &&
        ((variety.colorOptions ?? []).includes(perVariety.color) ||
          perVariety.color === "mix")
      ) {
        setColorChoice(perVariety.color);
      }
      return;
    }
    // Fallback: legacy length pref (Step 6.6). Unit fallback removed -
    // usePreferences store is already populated from Zustand persist on
    // mount, no need to re-read the legacy localStorage key.
    const savedLength = readLengthPreference(variety.lengths);
    if (savedLength !== null && savedLength !== length) setLength(savedLength);
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */

  const [toast, setToast] = useState<string | null>(null);

  function handleLength(next: number) {
    setLength(next);
    writeLengthPreference(next);
  }
  function handleUnit(next: UnitChoice) {
    // Step 6.7.3 / AC174: writes the global usePreferences store. Zustand
    // persist middleware mirrors to mag-portal-unit-pref localStorage.
    setGlobalUnit(next);
    setQuantity(1);
  }

  const stem = pricePerStem(effectiveVariety, length) ?? 0;
  const stemsBunch = variety.stemsPerBunch ?? 0;
  const activeBoxStems =
    capacity && boxCode ? stemsInBox(capacity, boxCode) ?? 0 : 0;

  // Active-unit math (the teal info pill).
  const activeMath = (() => {
    if (unit === "box") {
      const code = boxCode ?? "Box";
      const otherCodes = availableBoxCodes.filter((c) => c !== boxCode);
      const otherLine =
        otherCodes.length > 0 ? `or ${otherCodes.join(", ")} available` : "";
      const bunchesPerBox =
        stemsBunch > 0 ? activeBoxStems / stemsBunch : null;
      const bunchPart =
        bunchesPerBox !== null && Number.isFinite(bunchesPerBox)
          ? `${bunchesPerBox} bunches * `
          : "";
      return {
        main: `$${(stem * activeBoxStems).toFixed(2)} / ${code}`,
        sub: `${bunchPart}${activeBoxStems} stems${otherLine ? " * " + otherLine : ""}`,
      };
    }
    // Bunch unit
    const main = `$${(stem * stemsBunch).toFixed(2)} / bunch`;
    const codes = availableBoxCodes.join(" or ");
    return {
      main,
      sub: codes
        ? `${stemsBunch} stems * ships in ${codes}`
        : `${stemsBunch} stems`,
    };
  })();

  // Add button label per spec section 8d.
  const colorPart = colorChoice
    ? colorChoice === "mix"
      ? " of Mix"
      : ` of ${colorTitle(colorChoice)}`
    : "";
  const gradePart = isHydrangea && variety.grade ? ` ${variety.grade}` : "";
  const addLabel = (() => {
    if (unit === "box") {
      return `Add ${quantity} ${boxCode ?? "box"} of ${variety.name}${gradePart}${colorPart} to box`;
    }
    return `Add ${quantity} ${quantity === 1 ? "bunch" : "bunches"}${colorPart} to box`;
  })();

  const compatLabel =
    COMPAT_LABEL[variety.compatibilityGroup] ?? "other varieties";
  const compatHint =
    variety.compatibilityGroup === "A"
      ? "Ships in same box as Garden Roses"
      : variety.compatibilityGroup === "B"
        ? "Ships in spray rose boxes"
        : variety.compatibilityGroup === "C"
          ? "Ships in hydrangea boxes"
          : variety.compatibilityGroup === "D"
            ? "Ships in specialty boxes"
            : `Ships in ${compatLabel.toLowerCase()} boxes`;

  function placeIntoCart(forceAccept: boolean, forceNewBox: boolean) {
    // Step 6.7.3 / AC176: cart engine treats a single addToBox call as
    // ONE line that must fit in ONE box. In Box mode with quantity > 2
    // the implied stem count (qty * stemsPerBox) routinely exceeds even
    // HB capacity, so the engine throws and React swallows the error
    // (silent failure). Split Box-mode adds into per-box iterations so
    // each call always has a fitting line. Bunch mode keeps the
    // single-call path (bunches fit naturally up to box cap).
    const iterations = unit === "box" ? quantity : 1;
    const perIterQty = unit === "box" ? 1 : quantity;

    try {
      let runningCart = readCart();
      for (let i = 0; i < iterations; i++) {
        const outcome = addToBox(
          runningCart,
          {
            variety,
            length: isHydrangea ? undefined : length,
            unit,
            quantity: perIterQty,
            colorChoice: colorChoice || undefined,
            boxCode: unit === "box" ? boxCode ?? undefined : undefined,
          },
          { forceAccept, forceNewBox },
        );
        runningCart = outcome.cart;
      }
      writeCart(runningCart);
      // Step 6.7 section 7b: persist per-variety choice for next visit.
      writeVarietyPref(variety.slug, {
        color: colorChoice || undefined,
        length: isHydrangea ? undefined : length,
        grade: variety.grade,
        qty: quantity,
        unit,
        // DIRECTIVE #023: persist the buyer's resolved box size so the
        // next visit + the silent PowerBuyerRow default read it back.
        boxCode: boxCode ?? undefined,
      });
      setToast(`Added: ${addLabel.replace(/^Add /, "")}`);
      setTimeout(() => setToast(null), 4000);
    } catch (err) {
      // Last-resort surfacing of any unexpected engine throw. The Box-
      // mode split above prevents the common case (qty * boxStems >
      // HB cap); this catch covers Bunch-mode quantities so extreme
      // that even a single line of N bunches busts HB capacity (~12+
      // bunches of 25-stem rose varieties). Spec section 2's "silent
      // failures are not acceptable" rule applies.
      const msg = err instanceof Error ? err.message : String(err);
      setToast(`Couldn't add: ${msg}`);
      setTimeout(() => setToast(null), 5000);
    }
  }

  function handleAdd() {
    // DIRECTIVE #025: anonymous users get routed to /sign-in with
    // redirect=<current-path>. Cart writes are gated by auth state.
    if (priceGate.ready && !priceGate.canSeePrices) {
      const here = pathname ?? `/catalog/${variety.slug}`;
      router.push(`/sign-in?redirect=${encodeURIComponent(here)}`);
      return;
    }
    // #452: a sold-out selection cannot be added to a new cart.
    if (blockSoldOut) return;
    placeIntoCart(false, false);
  }

  // DIRECTIVE #025: gate prices + add-to-box behind auth.
  const priceGate = usePriceGate();
  const canSeePrices = priceGate.ready && priceGate.canSeePrices;
  const router = useRouter();
  const pathname = usePathname();

  // #452: is the currently-selected SKU (variety + color + length) sold out?
  // Re-evaluates as the buyer changes color/length via the shared cascade.
  const soldOut = useMemo(() => {
    if (!soldOutKeys || soldOutKeys.length === 0) return false;
    return isSoldOut(new Set(soldOutKeys), {
      slug: variety.slug,
      colorChoice: colorChoice || null,
      length: isHydrangea ? null : length,
    });
  }, [soldOutKeys, variety.slug, colorChoice, isHydrangea, length]);
  // Only block a signed-in buyer here; an anon buyer is already routed to
  // sign-in by the auth gate (and can't add anyway). The server order gate is
  // the safety net regardless.
  const blockSoldOut = canSeePrices && soldOut;

  return (
    <div className="space-y-5">
      {/* Stem-price canonical anchor. DIRECTIVE #025: gated for anon. */}
      <div data-testid="detail-stem-headline">
        {canSeePrices ? (
          <>
            <p
              data-testid="detail-price-stem"
              className="text-[32px] font-bold leading-tight tracking-tight text-brand-text"
            >
              ${stem.toFixed(2)}{" "}
              <span className="text-[14px] font-medium text-brand-text-muted">
                / stem
              </span>
            </p>
            <p
              data-testid="detail-stem-caption"
              className="mt-1 text-xs text-brand-text-muted"
            >
              Same per-stem price across all box sizes
            </p>
          </>
        ) : priceGate.ready ? (
          <div data-testid="detail-signed-out-price">
            <SignInForPricesCTA variant="block" />
            <p className="mt-2 text-xs text-brand-text-muted">
              Photos, packing details, and farm info are visible to
              everyone — wholesale pricing requires a Magnaflor account.
            </p>
          </div>
        ) : (
          <PriceGateSkeleton width="12rem" height="2rem" />
        )}
      </div>

      {hasColorPicker && (
        <div>
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-brand-text-muted">
            Color
          </p>
          <ColorPicker
            options={variety.colorOptions ?? []}
            active={colorChoice}
            allowMix={mixAllowedForUnit(variety, unit)}
            onChange={setColorChoice}
            variant="detail"
            testIdPrefix="detail-color"
          />
          {colorChoice === "mix" && (
            <p
              data-testid="detail-color-mix-label"
              className="mt-1 text-xs italic text-brand-text-muted"
            >
              {colorChoiceLabel("mix")}
            </p>
          )}
        </div>
      )}

      {showLengthPicker && (
        <div>
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-brand-text-muted">
            Length
          </p>
          <LengthPicker
            lengths={variety.lengths}
            active={length}
            onChange={handleLength}
          />
        </div>
      )}

      {/* Step 6.7: terminology unified to "Buy by", Box first per section 3c. */}
      {availableUnits.length > 1 && (
        <div>
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-brand-text-muted">
            Buy by
          </p>
          <div
            data-testid="detail-unit-toggle"
            className="inline-flex rounded-lg border border-brand-border bg-brand-surface p-0.5"
          >
            <button
              type="button"
              data-testid="detail-unit-box"
              aria-pressed={unit === "box"}
              onClick={() => handleUnit("box")}
              className={
                unit === "box"
                  ? "rounded-md bg-brand-teal px-5 py-2 text-sm font-bold text-white shadow-sm"
                  : "rounded-md px-5 py-2 text-sm font-bold text-brand-text-muted"
              }
            >
              Box
            </button>
            <button
              type="button"
              data-testid="detail-unit-bunch"
              aria-pressed={unit === "bunch"}
              onClick={() => handleUnit("bunch")}
              className={
                unit === "bunch"
                  ? "rounded-md bg-brand-teal px-5 py-2 text-sm font-bold text-white shadow-sm"
                  : "rounded-md px-5 py-2 text-sm font-bold text-brand-text-muted"
              }
            >
              Bunch
            </button>
          </div>
        </div>
      )}

      {/* Box-size sub-picker when relevant. Step 6.7.1: render even for
          single-box varieties so the buyer always sees the box size + stem
          count (Asiatic Lily QB 70, Oriental Lily QB 60). */}
      {(unit === "box" || isHydrangea) && availableBoxCodes.length >= 1 && (
        <div data-testid="detail-box-size-picker">
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-brand-text-muted">
            Box size
          </p>
          <div className="flex gap-2">
            {availableBoxCodes.map((code) => {
              const isActive = code === boxCode;
              return (
                <button
                  key={code}
                  type="button"
                  data-testid={`detail-box-size-${code}`}
                  aria-pressed={isActive}
                  onClick={() => setBoxCode(code)}
                  className={
                    isActive
                      ? "rounded-md bg-brand-teal px-3.5 py-1.5 text-sm font-bold text-white"
                      : "rounded-md border border-brand-border bg-white px-3.5 py-1.5 text-sm font-bold text-brand-text-muted"
                  }
                >
                  {code} ({capacity?.[code]} stems)
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Active-unit math teal info pill. DIRECTIVE #025: hidden for
          anon (the main carries a $ amount). */}
      {canSeePrices && (
        <div
          data-testid="detail-active-pill"
          className="rounded-md border-l-[3px] border-brand-teal bg-brand-teal-soft px-3.5 py-3"
        >
          <div
            data-testid="detail-active-main"
            className="text-[22px] font-bold leading-tight tracking-tight text-brand-text"
          >
            {activeMath.main}
          </div>
          <div
            data-testid="detail-active-sub"
            className="mt-1 text-xs text-brand-teal-dark"
          >
            {activeMath.sub}
          </div>
        </div>
      )}

      {/* Quantity stepper */}
      <div>
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-brand-text-muted">
          Quantity
        </p>
        <div
          data-testid="detail-qty-stepper"
          className="inline-flex items-center overflow-hidden rounded-lg border border-brand-border"
        >
          <button
            type="button"
            data-testid="detail-qty-decrement"
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            className="h-10 w-9 bg-brand-surface text-lg text-brand-text-muted"
          >
            -
          </button>
          <input
            data-testid="detail-qty-value"
            data-unit={unit}
            aria-label="Quantity"
            value={quantity}
            readOnly
            className="h-10 w-16 border-0 text-center text-base font-bold"
          />
          <button
            type="button"
            data-testid="detail-qty-increment"
            onClick={() => setQuantity((q) => Math.min(99, q + 1))}
            className="h-10 w-9 bg-brand-surface text-lg text-brand-text-muted"
          >
            +
          </button>
        </div>
      </div>

      <button
        type="button"
        data-testid="detail-add-to-box"
        data-unit={unit}
        data-quantity={quantity}
        data-auth-gated={
          priceGate.ready && !canSeePrices ? "true" : undefined
        }
        data-sold-out={blockSoldOut ? "true" : undefined}
        disabled={blockSoldOut}
        onClick={handleAdd}
        aria-label={
          blockSoldOut
            ? "Sold out"
            : priceGate.ready && !canSeePrices
              ? "Sign in to add"
              : undefined
        }
        className={
          "w-full rounded-xl px-4 py-3.5 text-base font-bold shadow-md " +
          (blockSoldOut
            ? "cursor-not-allowed bg-brand-surface text-brand-text-muted"
            : "text-white " +
              (priceGate.ready && !canSeePrices
                ? "bg-brand-teal/70 hover:bg-brand-teal"
                : "bg-brand-teal hover:bg-brand-teal/90"))
        }
      >
        {blockSoldOut
          ? "Sold out"
          : priceGate.ready && !canSeePrices
            ? "Sign in to add"
            : addLabel}
      </button>

      <p
        data-testid="detail-compatibility-hint"
        className="text-center text-xs italic text-brand-text-muted"
      >
        {compatHint}
      </p>

      {toast && (
        <QuickAddToast message={toast} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}
